import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { forkJoin } from 'rxjs'

import {
  MonthsCountType,
  SubscriptionPlans,
  SubscriptionPriceService,
  SubscriptionResponseType
} from '../../services/subscription-price.service'

import { ResultCode } from '../../domain/authentication.model'
import { Subscription } from '../../domain/user'
import { AuthService } from '../../services/auth.service'
import { InfoPopupService } from '../../services/info-popup.service'
import { NotificationService } from '../../services/notification.service'
import { SsrService } from '../../services/ssr.service'
import { StartSellingService } from '../../services/start-selling.service'
import { SubscriptionFeatureType } from '../../types/subscription-feature.type'

@Component({
  selector: 'bmcore-tariff-block',
  templateUrl: './bmcore-tariff-block.component.html',
  styleUrls: ['./bmcore-tariff-block.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BmcoreTariffBlockComponent implements OnInit {
  features: SubscriptionFeatureType[] = [
    {
      title: 'Upload Tracks Limit',
      value: {
        premium: '100',
        expert: 'Unlimited'
      }
    },
    {
      title: 'Personal Site',
      value: {
        premium: true,
        expert: true
      }
    },
    {
      title: 'Sales Statistics',
      value: {
        premium: true,
        expert: true
      }
    },
    {
      title: 'Advertising On The Home Page',
      value: {
        premium: false,
        expert: true
      }
    },
    {
      title: 'Widget',
      value: {
        premium: 'Coming soon',
        expert: 'Coming soon'
      }
    }
  ]
  premiumPrice: SubscriptionResponseType
  expertPrice: SubscriptionResponseType
  currentMonthCount: MonthsCountType = 1
  currentMonthId = 1
  currentPlan: SubscriptionPlans

  code: string | null
  userId: number | null

  isUserAuthorized: boolean
  isBeatmaker: boolean
  subscription: Subscription

  isSelectButtonsDisabled = false

  isLoader = false

  constructor(
    private subscriptionPriceService: SubscriptionPriceService,
    private activatedRoute: ActivatedRoute,
    private notificationService: NotificationService,
    private authService: AuthService,
    private infoPopupService: InfoPopupService,
    private startSellingService: StartSellingService,
    private ssrService: SsrService
  ) {}

  ngOnInit(): void {
    const code = this.activatedRoute.snapshot.queryParams['code']
    const userId = this.activatedRoute.snapshot.queryParams['userId']

    this.code = code ? code : null
    this.userId = userId ? Number(userId) : null

    this.ssrService.wait('bmcore-tariff-block')
    this.getPriceForSubscriptions()

    this.authService.user.subscribe(res => {
      this.isUserAuthorized = res.isAuth
      this.isBeatmaker = res.isBeatmaker
      this.subscription = res.beatmaker?.subscription
    })
  }

  getPriceForSubscriptions() {
    this.isLoader = true
    forkJoin([
      this.subscriptionPriceService.getPriceForSubscriptions({
        monthes: this.currentMonthCount,
        plan: SubscriptionPlans.Premium,
        couponCode: this.code,
        userId: this.userId
      }),
      this.subscriptionPriceService.getPriceForSubscriptions({
        monthes: this.currentMonthCount,
        plan: SubscriptionPlans.Expert,
        couponCode: this.code,
        userId: this.userId
      })
    ]).subscribe(([res1, res2]) => {
      this.ssrService.ready('bmcore-tariff-block')
      this.premiumPrice = res1.data
      this.expertPrice = res2.data
      this.isLoader = false
    })
  }

  changeCurrentMonth(monthId: number) {
    this.currentMonthId = monthId
    this.getMonthCount()
    this.getPriceForSubscriptions()
  }

  getMonthCount() {
    switch (this.currentMonthId) {
      case 2:
        this.currentMonthCount = 3
        break
      case 3:
        this.currentMonthCount = 12
        break
      case 4:
        this.currentMonthCount = 36
        break
      default:
        this.currentMonthCount = 1
        break
    }
  }

  selectPlan(plan: 'premium' | 'expert') {
    this.isSelectButtonsDisabled = true
    switch (plan) {
      case 'premium':
        this.currentPlan = SubscriptionPlans.Premium
        break

      case 'expert':
        this.currentPlan = SubscriptionPlans.Expert
        break
    }
    this.makeSubscriptionPayment()
  }

  makeSubscriptionPayment() {
    if (!this.isUserAuthorized && !this.code && !this.userId) {
      this.infoPopupService.notify('“Select a subscription”')
    } else if (!this.isBeatmaker && !this.code && !this.userId) {
      this.startSellingService.notify()
    } else if (
      this.isBeatmaker ||
      (this.code && this.userId && !this.isUserAuthorized) ||
      (this.code && this.userId && !this.isBeatmaker)
    ) {
      this.subscriptionPriceService
        .makeSubscriptionPayment({
          monthes: this.currentMonthCount,
          plan: this.currentPlan,
          subscriptionCoupon: this.code
        })
        .subscribe(
          res => {
            if (res.resultCode === ResultCode.Success) {
              document.location.href = res.data.redirectUrl
            } else {
              this.notificationService.handleError(res.messages[0])
            }
          },
          err => {
            this.notificationService.handleError(err.message)
            this.isSelectButtonsDisabled = false
          },
          () => {
            this.isSelectButtonsDisabled = false
          }
        )
    }
  }

  getSubscriptionPlan(plan: SubscriptionPlans) {
    return SubscriptionPlans[plan]
  }
}
